import { apiFetch } from "./client";
import { comprimiFoto } from "@/lib/comprimiFoto";
import type { Analisi, AnalisiSessione } from "./analisiBolletta";

export interface QrAgenzia {
  agenzia: string;
  turnstile_site_key: string;
}

export interface QrSessione {
  session_id: string;
  cat_ws_url: string;
}

export const qrApi = {
  agenzia(token: string): Promise<QrAgenzia> {
    return apiFetch<QrAgenzia>(`/qr/${token}`);
  },

  sessione(token: string, turnstileToken: string): Promise<QrSessione> {
    return apiFetch<QrSessione>(`/qr/${token}/sessione`, {
      method: "POST",
      body: JSON.stringify({ turnstile_token: turnstileToken, presa_visione: true }),
    });
  },

  // Raw fetch con FormData, come uploadChatFile: niente Content-Type manuale
  // (il boundary del multipart lo aggiunge il browser), e fetch non lancia sui
  // 4xx/5xx da solo.
  async upload(
    token: string,
    sessionId: string,
    file: File,
    kind: "bolletta" | "documento",
    opzioni: { pagina?: number; trattieni?: boolean } = {},
  ): Promise<{ analisiId: string | null; uploadId: string | null }> {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("kind", kind);
    // posto della foto nella bolletta (da 1): solo le foto, mai un PDF
    if (opzioni.pagina !== undefined) form.append("pagina", String(opzioni.pagina));
    // passi foto: la foto resta sul portale e passa al Cat con `consegna`, dopo il batch della chat
    if (opzioni.trattieni) form.append("trattieni", "true");
    // Foto più leggere prima di partire (lib/comprimiFoto): vale per tutti gli invii del
    // QR, sia al passo foto sia per gli allegati a metà chat.
    form.append("file", await comprimiFoto(file));
    const res = await fetch(`/api/qr/${token}/upload`, {
      method: "POST",
      credentials: "include",
      body: form,
    });
    if (!res.ok) {
      throw new Error(`upload_failed ${res.status} ${file.name}`);
    }
    // l'analisi della bolletta, se accesa per l'agenzia (null altrimenti o per le pagine successive)
    const corpo = await res.json().catch(() => ({}));
    return {
      analisiId: typeof corpo?.analisi_id === "string" ? corpo.analisi_id : null,
      uploadId: typeof corpo?.upload_id === "string" ? corpo.upload_id : null,
    };
  },

  /** Il cliente ha finito le foto della bolletta: l'analisi le aspetta tutte e le legge insieme. */
  bollettaCompleta(token: string, sessionId: string, pagine: number): Promise<{ ok: boolean }> {
    return apiFetch<{ ok: boolean }>(`/qr/${token}/bolletta-completa`, {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, pagine }),
    });
  },

  /** Dopo `[batch_inizio]`: i file trattenuti passano al Cat in quest'ordine, ognuno una volta sola. */
  consegna(token: string, sessionId: string, file: string[]): Promise<{ ok: boolean; inoltrati: number }> {
    return apiFetch<{ ok: boolean; inoltrati: number }>(`/qr/${token}/consegna`, {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId, file }),
    });
  },

  /** Analisi delle bollette di questa sessione QR (lette con la sessione firmata del cliente). */
  analisiSessione(token: string, sessionId: string): Promise<AnalisiSessione[]> {
    return apiFetch<AnalisiSessione[]>(`/qr/${token}/analisi?session_id=${encodeURIComponent(sessionId)}`);
  },

  analisi(token: string, sessionId: string, id: string): Promise<Analisi> {
    return apiFetch<Analisi>(`/qr/${token}/analisi/${encodeURIComponent(id)}?session_id=${encodeURIComponent(sessionId)}`);
  },
};
